import { BridgeApi, Configuration, LendborrowApi as ConsolidatedLendborrowApi, DexApi, SupportApi, } from "@blockdaemon/defi-api-sdk";
import { normalizeOptionalChainIdToCAIP2 } from "../../shared/caip2";
import { DEFAULT_BASE_PATH } from "../../shared/constants";
import { handleDefiSdkError } from "../../shared/error";
import { getLogger } from "../../shared/logger";
import { buildPortfolioTokensRequestUrl } from "./mappings";
/**
 * Logger instance for DefiApi operations.
 *
 * @remarks
 * This logger is configured specifically for DeFi API operations and provides
 * detailed debugging information for API requests, responses, and error conditions.
 * It uses the Hyperledger Cactus LoggerProvider for consistent logging across
 * the SDK.
 *
 * **Log Levels:**
 * - DEBUG: API URLs, request parameters, response data
 * - INFO: Successful operations, status codes
 * - WARN: Retries, fallback operations
 * - ERROR: API failures, network errors
 *
 * @example
 * ```typescript
 * // Logger outputs for a successful API call:
 * // DEBUG: DefiApi.getPrice - URL: https://svc.blockdaemon.com/portfolio/v1/tokens?tokenAddress=0x...
 * // DEBUG: DefiApi.getPrice - API Key length: 46
 * // INFO: DefiApi.getPrice - Response status: 200
 * // DEBUG: DefiApi.getPrice - Response data: {...}
 * ```
 *
 * @internal
 */
/**
 * Internal DeFi API client (thin wrappers over the consolidated SDK).
 *
 * @remarks
 * - This class is the INTERNAL implementation layer. It performs only authentication setup
 *   and try/catch around consolidated SDK calls, returning RAW SDK payloads or structured errors.
 * - No validation, request mapping, or response transformation happens here.
 * - High-level business logic, validation, and transformations live in the upper layers
 *   such as `DefiModule` and the helpers under `modules/common`.
 *
 * High-level usage examples are available on the public `DefiModule`. See that class for
 * feature overviews and guided examples.
 *
 * @category Internal
 * @internal
 */
export class DefiApi {
    bdApiKey;
    basePath;
    logger;
    dexApi;
    bridgeApi;
    lendborrowApi;
    supportApi;
    /**
     * Creates a new DefiApi instance with the specified configuration.
     *
     * @param options - Configuration options including API key and base path
     * @remarks
     * The `bdApiKey` is optional. When it is not provided, the client will still
     * initialize successfully but will emit a warning. Some authenticated
     * endpoints may return 401 Unauthorized without a valid API key.
     *
     * @example
     * ```typescript
     * const api = new DefiApi({
     *   bdApiKey: process.env.BD_API_KEY,
     *   basePath: "https://svc.blockdaemon.com" // optional
     * });
     * ```
     */
    constructor(options) {
        this.bdApiKey = options.bdApiKey;
        this.basePath = options.basePath || DEFAULT_BASE_PATH;
        this.logger = getLogger({
            label: "defi-api",
            level: process.env.LOG_LEVEL || "debug",
        });
        if (!this.bdApiKey || this.bdApiKey.trim() === "") {
            this.logger.warn("BD API key is not set. DeFi API requests may fail with 401 Unauthorized.");
        }
        const configuration = new Configuration({
            apiKey: this.bdApiKey,
            basePath: this.basePath,
        });
        this.dexApi = new DexApi(configuration);
        this.bridgeApi = new BridgeApi(configuration);
        this.lendborrowApi = new ConsolidatedLendborrowApi(configuration);
        this.supportApi = new SupportApi(configuration);
    }
    /**
     * Internal helper: makes HTTP request to portfolio tokens API endpoint.
     *
     * @param request - Normalized request parameters
     * @returns Promise resolving to token response or error
     * @internal
     */
    async callPortfolioTokensApi(request) {
        const url = buildPortfolioTokensRequestUrl(this.basePath, request);
        const headers = {
            Accept: "application/json",
        };
        if (this.bdApiKey) {
            headers.Authorization = `Bearer ${this.bdApiKey}`;
        }
        this.logger.debug(`Calling portfolio tokens API: ${url.toString()}`);
        const response = await fetch(url.toString(), {
            method: "GET",
            headers,
        });
        if (!response.ok) {
            let errorMessage = `Portfolio tokens API error: ${response.status} ${response.statusText}`;
            const contentType = response.headers.get("content-type");
            try {
                if (contentType?.includes("application/json")) {
                    const errorData = await response.json();
                    errorMessage += ` - ${JSON.stringify(errorData)}`;
                }
                else {
                    const errorText = await response.text();
                    if (errorText) {
                        errorMessage += ` - ${errorText}`;
                    }
                }
            }
            catch (parseError) {
                // If parsing fails, include the parse error in the message
                errorMessage += ` - Failed to parse error response: ${parseError}`;
            }
            throw new Error(errorMessage);
        }
        const data = await response.json();
        return data;
    }
    /**
     * Internal: raw assets lookup via portfolio API endpoint.
     * - No validation or transformation. Returns the API token map as-is.
     *
     * @remarks
     * - Calls `/portfolio/v1/tokens` endpoint with the provided request (chainID/address/symbol).
     * - Normalizes optional `chainId` into CAIP-2 `chainID` before forwarding.
     * @internal
     */
    async getAssets(request) {
        try {
            const reqNormalized = {
                ...request,
            };
            const requested = (reqNormalized.chainID ?? reqNormalized.chainId);
            const caip2 = normalizeOptionalChainIdToCAIP2(requested, "eip155");
            if (caip2) {
                reqNormalized.chainID = caip2;
                if ("chainId" in reqNormalized)
                    reqNormalized.chainId = undefined;
            }
            const response = await this.callPortfolioTokensApi(reqNormalized);
            return response;
        }
        catch (error) {
            return handleDefiSdkError(error, this.logger);
        }
    }
    /**
     * Retrieves swap quotes from multiple DEXs for a specified token path.
     *
     * @param request - The swap quote request parameters
     * @returns Promise resolving to aggregated quote data or structured error response
     *
     * @example
     * ```typescript
     * const quotes = await api.buildSwapQuote({
     *   chainId: "1",
     *   path: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2,0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
     *   amountIn: "1000000000000000000"
     * });
     * ```
     * @internal
     */
    async buildSwapQuote(request) {
        // Upstream: DexApi.dexQuotes
        return this.dexApi.dexQuotes(request);
    }
    /**
     * Retrieves bridge quotes from multiple bridges for cross-chain token transfers.
     *
     * @remarks
     * Tokens must be specified by either (srcTokenSymbol + dstTokenSymbol) or (srcTokenAddress + dstTokenAddress), not both.
     *
     * @param request - The bridge quote request parameters
     * @returns Promise resolving to aggregated bridge quote data
     * @throws When required parameters are missing or an API error occurs
     *
     * @example
     * ```typescript
     * const bridgeQuotes = await api.buildBridgeQuote({
     *   dstChainId: "137",
     *   srcTokenSymbol: "USDC",
     *   dstTokenSymbol: "USDC",
     *   amountIn: "1000000" // 1 USDC (6 decimals)
     * });
     * console.log(`Found ${bridgeQuotes.data.length} bridge quotes`);
     * ```
     * @internal
     */
    async buildBridgeQuote(request) {
        // Upstream: BridgeApi.bridgeQuotes
        return this.bridgeApi.bridgeQuotes(request);
    }
    /**
     * Builds swap transaction with approval for a specified token pair.
     *
     * @param request - The swap transaction request parameters
     * @returns Promise resolving to transaction data
     * @throws When required parameters are missing or an API error occurs
     *
     * @example
     * ```typescript
     * const swapTransaction = await api.buildSwapTransactionWithApproval({
     *   dexId: "uniswap_v3",
     *   path: ["0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2", "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48"],
     *   amountIn: "1000000000000000000", // 1 WETH
     *   amountOutMin: "3800000000", // Minimum 3800 USDC
     *   to: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *   from: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *   deadline: "1700000000",
     *   slippage: "1"
     * });
     * console.log("Transaction data:", swapTransaction.data);
     * ```
     * @internal
     */
    async buildSwapTransactionWithApproval(request) {
        // Upstream: DexApi.dexSwapWithApproval
        return this.dexApi.dexSwapWithApproval(request);
    }
    /**
     * Builds bridge transaction with approval for cross-chain token transfers.
     *
     * @remarks
     * Tokens must be specified by either (srcTokenSymbol + dstTokenSymbol) or (srcTokenAddress + dstTokenAddress), not both.
     *
     * @param request - The bridge transaction request parameters
     * @returns Promise resolving to transaction data
     * @throws When required parameters are missing or an API error occurs
     *
     * @example
     * ```typescript
     * const bridgeTransaction = await api.buildBridgeTransactionWithApproval({
     *   bridgeId: "stargate",
     *   srcChainId: "1",
     *   dstChainId: "137",
     *   srcTokenSymbol: "USDC",
     *   dstTokenSymbol: "USDC",
     *   amountIn: "1000000", // 1 USDC
     *   amountOutMin: "999000", // Minimum 0.999 USDC
     *   from: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *   to: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *   slippage: "1"
     * });
     * console.log("Transaction data:", bridgeTransaction.data);
     * ```
     * @internal
     */
    async buildBridgeTransactionWithApproval(request) {
        // Upstream: BridgeApi.bridgeSwapWithApproval
        return this.bridgeApi.bridgeSwapWithApproval(request);
    }
    /**
     * Retrieves the status of a bridge transaction using its transaction hash.
     *
     * @remarks
     * This method fetches detailed information about a cross-chain bridge transaction
     * including its current status, source and destination details, and transaction metadata.
     * It's useful for monitoring the progress of bridge transfers and verifying completion.
     *
     * **Features:**
     * - Real-time transaction status tracking
     * - Cross-chain transaction details
     * - Comprehensive transaction metadata
     * - Bridge-specific information
     *
     * **Required Parameters:**
     * - `transactionHash`: The hash of the bridge transaction
     * - `bridgeId`: The identifier of the bridge used
     *
     * **Optional Parameters:**
     * - `srcChainId`: Source chain identifier for additional context
     * - `srcChainSymbol`: Source chain symbol for additional context
     *
     * @param request - The bridge status request parameters
     * @returns Promise resolving to bridge status data
     * @throws When required parameters are missing or an API error occurs
     * @internal
     *
     * @example
     * **Basic Status Query**
     * ```typescript
     * const status = await api.getBridgeStatus({
     *   transactionHash: "0x1234567890abcdef...",
     *   bridgeId: "stargate"
     * });
     * console.log("Transaction status:", status.data.status);
     * ```
     * @internal
     */
    async getBridgeStatus(request) {
        // Upstream: BridgeApi.bridgeTransactions
        return this.bridgeApi.bridgeTransactions(request);
    }
    /**
     * Retrieves comprehensive DeFi positions for a specified user address.
     *
     * @remarks
     * This method fetches all DeFi positions associated with a user's address across
     * various protocols including lending, borrowing, liquidity provision, and staking.
     * The positions include real-time USD valuations and detailed position metadata.
     *
     * **Features:**
     * - Multi-protocol position aggregation
     * - Real-time USD value calculation for all positions
     * - Position type classification (lending, borrowing, liquidity, staking)
     * - Cross-chain position support
     * - Detailed position metadata and analytics
     *
     * **Supported Position Types:**
     * - Lending positions (Aave, Compound, etc.)
     * - Borrowing positions with collateral details
     * - Liquidity provider positions (Uniswap, SushiSwap, etc.)
     * - Staking positions (protocol-specific staking)
     * - Yield farming positions
     *
     * @param request - The user positions query parameters
     * @returns Promise resolving to user positions data or structured error response
     * @throws {Error} When the address parameter is missing or invalid
     *
     * @example
     * **Basic Position Query**
     * ```typescript
     * const positions = await api.getUserPositions({
     *   address: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6"
     * });
     *
     * if (!isBlockdaemonSDKError(positions)) {
     *   console.log("Total value USD:", positions.data.totalValueUSD);
     *   console.log("Number of positions:", positions.data.positions.length);
     *
     *   positions.data.positions.forEach(position => {
     *     console.log(`${position.protocol} ${position.type}: $${position.valueUSD}`);
     *   });
     * }
     * ```
     *
     * @example
     * **Chain-Specific Position Query**
     * ```typescript
     * // Get positions only on Ethereum mainnet
     * const ethPositions = await api.getUserPositions({
     *   address: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *   chainId: "1"
     * });
     * ```
     *
     * @example
     * **Position Analysis**
     * ```typescript
     * const positions = await api.getUserPositions({
     *   address: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6"
     * });
     *
     * if (!isBlockdaemonSDKError(positions)) {
     *   // Group positions by protocol
     *   const positionsByProtocol = positions.data.positions.reduce((acc, pos) => {
     *     acc[pos.protocol] = (acc[pos.protocol] || 0) + Number.parseFloat(pos.valueUSD);
     *     return acc;
     *   }, {} as Record<string, number>);
     *
     *   console.log("Positions by protocol:", positionsByProtocol);
     * }
     * ```
     *
     * @see the response structure for user positions
     * @internal
     */
    async getUserPositions(request) {
        return this.lendborrowApi.lendborrowUserpositions({
            ...request,
            useCustomVaults: true,
        });
    }
    /**
     * Creates a lendborrow deposit transaction using the DeFi API.
     *
     * @remarks
     * This method creates a transaction for depositing assets into lending and borrowing
     * protocols through Blockdaemon's DeFi infrastructure. It supports various protocols
     * including Compound, Aave, and Morpho with customizable gas settings.
     *
     * **Supported Protocols:**
     *
     * **Ethereum:**
     * - Aave V2 (lendborrowId: "1000")
     * - Aave V3 (lendborrowId: "1200")
     * - Compound V2 (lendborrowId: "1100")
     * - Compound V3 (lendborrowId: "1300")
     * - Morpho (lendborrowId: "1400")
     *
     * **Ethereum Testnet Sepolia:**
     * - Aave V3 Testnet (lendborrowId: "1202")
     * - Compound V3 Testnet (lendborrowId: "1302")
     *
     * **Avalanche:**
     * - Aave V2 (lendborrowId: "1003")
     * - Aave V3 (lendborrowId: "1203")
     *
     * **Polygon:**
     * - Aave V2 (lendborrowId: "1005")
     * - Aave V3 (lendborrowId: "1205")
     *
     * **Arbitrum:**
     * - Aave V3 (lendborrowId: "1204")
     *
     * **Base:**
     * - Aave V3 (lendborrowId: "1206")
     * - Morpho (lendborrowId: "1402")
     *
     * **Optimism:**
     * - Aave V3 (lendborrowId: "1207")
     *
     * **Key Features:**
     * - Automatic gas estimation and optimization
     * - Support for collateral and non-collateral deposits
     * - Market-specific configurations for Morpho protocol
     * - Flexible fee handling with basis points transactions
     *
     * @param request - The lendborrow deposit request parameters
     * @returns Promise resolving to deposit transaction data
     * @throws When required parameters are missing or an API error occurs
     *
     * @example
     * **Basic Compound V2 Deposit**
     * ```typescript
     * const deposit = await api.lendborrowDeposit({
     *   lendborrowDepositRequestPayload: {
     *     lendborrowId: "1100", // Compound V2 on Ethereum
     *     asset: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
     *     amount: "1000000", // 1 USDC (6 decimals)
     *     onBehalfOf: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *     from: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6"
     *   }
     * });
     * console.log("Transaction data:", deposit.data?.txData);
     * ```
     *
     * @example
     * **Morpho Vault Deposit with Gas Configuration**
     * ```typescript
     * const deposit = await api.lendborrowDeposit({
     *   lendborrowDepositRequestPayload: {
     *     lendborrowId: "1400", // Morpho on Ethereum
     *     asset: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2", // WETH
     *     amount: "1000000000000000000", // 1 WETH (18 decimals)
     *     onBehalfOf: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *     from: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *     vaultAddress: "0x...",
     *     type: "vault",
     *     gas: "500000",
     *     gasPriority: "high",
     *     enableFee: true
     *   }
     * });
     * ```
     *
     * @internal
     */
    async lendborrowDeposit(request) {
        // Upstream: LendborrowApi.lendborrowDeposit
        return this.lendborrowApi.lendborrowDeposit({
            lendborrowDepositRequestPayload: {
                ...request.lendborrowDepositRequestPayload,
                useCustomVaults: true,
            },
        });
    }
    /**
     * Processes the withdrawal of deposited assets from the lending protocol.
     *
     * @remarks
     * This method allows users to withdraw their deposited assets from various lending protocols
     * such as Aave, Compound, and others. It handles the withdrawal transaction creation
     * with customizable gas parameters for efficient execution.
     *
     * **Features:**
     * - Multi-protocol support (Aave, Compound, etc.)
     * - Customizable gas parameters for cost optimization
     * - Automatic transaction validation and formatting
     * - Cross-chain lending protocol support
     * - Real-time withdrawal status tracking
     *
     * **Supported Protocols:**
     * - Aave V2 (lendborrowId: "1000") - Ethereum
     * - Aave V3 (lendborrowId: "1200") - Ethereum
     * - Compound V2 (lendborrowId: "1100") - Ethereum
     * - Compound V3 (lendborrowId: "1300") - Ethereum
     * - Morpho (lendborrowId: "1400") - Ethereum
     * - Aave V3 Testnet (lendborrowId: "1202") - Ethereum Sepolia
     * - Compound V3 Testnet (lendborrowId: "1302") - Ethereum Sepolia
     * - Aave V2 (lendborrowId: "1003") - Avalanche
     * - Aave V3 (lendborrowId: "1203") - Avalanche
     * - Aave V2 (lendborrowId: "1005") - Polygon
     * - Aave V3 (lendborrowId: "1205") - Polygon
     * - Aave V3 (lendborrowId: "1204") - Arbitrum
     * - Aave V3 (lendborrowId: "1206") - Base
     * - Morpho (lendborrowId: "1402") - Base
     * - Aave V3 (lendborrowId: "1207") - Optimism
     *
     * @param request - The withdrawal request parameters
     * @returns Promise resolving to withdrawal transaction data or structured error response
     * @throws {Error} When required parameters are missing or invalid
     *
     * @example
     * **Basic Withdrawal Request**
     * ```typescript
     * const withdrawal = await api.lendBorrowWithdraw({
     *   lendborrowWithdrawRequestPayload: {
     *     lendborrowId: "1000", // Aave V2 on Ethereum
     *     asset: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
     *     amount: "1000000", // 1 USDC (6 decimals)
     *     to: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6"
     *   }
     * });
     *
     * if (!isBlockdaemonSDKError(withdrawal)) {
     *   console.log("Withdrawal transaction:", withdrawal.data);
     * }
     * ```
     *
     * @example
     * **Withdrawal with Gas Configuration**
     * ```typescript
     * const withdrawal = await api.lendBorrowWithdraw({
     *   lendborrowWithdrawRequestPayload: {
     *     lendborrowId: "1000", // Aave V2 on Ethereum
     *     asset: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
     *     amount: "1000000",
     *     to: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *     gas: "300000",
     *     gasPriority: "high"
     *   }
     * });
     * ```
     *
     * @see {@link ILendBorrowWithdrawResponse} for response structure
     * @internal
     */
    async lendBorrowWithdraw(request) {
        return this.lendborrowApi.lendborrowWithdraw({
            lendborrowWithdrawRequestPayload: {
                ...request.lendborrowWithdrawRequestPayload,
                useCustomVaults: true,
            },
        });
    }
    /**
     * Retrieves current lending and borrowing rates (APYs) for a specified asset within a pool.
     *
     * @remarks
     * This method fetches key metrics such as variable/stable borrow and supply rates,
     * loan-to-value ratios, available liquidity, and reserve size. This data is essential
     * for evaluating the attractiveness and risk of entering or exiting a market.
     *
     * **Features:**
     * - Real-time APY data for lending and borrowing
     * - Variable and stable rate information
     * - Loan-to-value ratios and risk metrics
     * - Available liquidity and reserve size data
     * - Multi-protocol support (Aave, Compound, etc.)
     *
     * **Supported Protocols:**
     * - Aave V2 (lendborrowId: "1000") - Ethereum
     * - Aave V3 (lendborrowId: "1200") - Ethereum
     * - Compound V2 (lendborrowId: "1100") - Ethereum
     * - Compound V3 (lendborrowId: "1300") - Ethereum
     * - Morpho (lendborrowId: "1400") - Ethereum
     * - And many more across different chains
     *
     * @param request - The lendborrow pool request parameters
     * @returns Promise resolving to pool data or structured error response
     * @throws {Error} When required parameters are missing or invalid
     *
     * @example
     * **Basic Pool Query**
     * ```typescript
     * const pool = await api.getLendborrowPool({
     *   asset: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48" // USDC
     * });
     *
     * if (!isBlockdaemonSDKError(pool)) {
     *   console.log("Pool data:", pool.data);
     * }
     * ```
     *
     * @example
     * **Pool Query with Protocol and Market**
     * ```typescript
     * const pool = await api.getLendborrowPool({
     *   asset: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
     *   lendborrowId: "1000", // Aave V2
     *   marketId: "usdc",
     *   type: "lending"
     * });
     * ```
     *
     * @see {@link IGetLendborrowPoolResponse} for response structure
     * @internal
     */
    async getLendborrowPool(request) {
        return this.lendborrowApi.lendborrowPool({
            ...request,
            useCustomVaults: true,
        });
    }
    /**
     * Retrieves comprehensive overview of supply and borrow APYs across multiple assets in lending pools.
     *
     * @remarks
     * This method fetches aggregated data such as interest rates, liquidity, reserve sizes,
     * and token-specific details (like symbols and addresses) to help compare opportunities
     * across different markets. It provides a comprehensive view of multiple lending pools
     * in a single request.
     *
     * **Features:**
     * - Multi-asset pool data aggregation
     * - Supply and borrow APY comparison
     * - Interest rates and liquidity information
     * - Reserve sizes and token details
     * - Multi-protocol support (Aave, Compound, etc.)
     *
     * **Supported Protocols:**
     * - Aave V2 (lendborrowId: "1000") - Ethereum
     * - Aave V3 (lendborrowId: "1200") - Ethereum
     * - Compound V2 (lendborrowId: "1100") - Ethereum
     * - Compound V3 (lendborrowId: "1300") - Ethereum
     * - Morpho (lendborrowId: "1400") - Ethereum
     * - And many more across different chains
     *
     * @param request - The lendborrow pools request parameters
     * @returns Promise resolving to pools data
     * @throws {Error} When required parameters are missing or invalid
     *
     * @example
     * **Basic Pools Query**
     * ```typescript
     * const pools = await api.getLendborrowPools({
     *   assets: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48,0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2" // USDC,WETH
     * });
     *
     * console.log("Pools data:", pools.data);
     * ```
     *
     * @example
     * **Pools Query with Protocol and Markets**
     * ```typescript
     * const pools = await api.getLendborrowPools({
     *   assets: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48,0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2",
     *   lendborrowId: "1000", // Aave V2
     *   markets: "usdc,weth"
     * });
     * ```
     *
     * @see {@link IGetLendborrowPoolsResponse} for response structure
     * @internal
     */
    async getLendborrowPools(request) {
        // Upstream: LendborrowApi.lendborrowPools
        return this.lendborrowApi.lendborrowPools({
            ...request,
            useCustomVaults: true,
        });
    }
    /**
     * Retrieves API metadata for DeFi protocols and services.
     *
     * @remarks
     * This method fetches metadata about supported protocols including names, logos,
     * and chain information. It's useful for populating UI elements and understanding
     * which protocols are available on different chains.
     *
     * **Features:**
     * - Protocol names and identifiers
     * - Logo URLs for UI display
     * - Supported chains information
     * - Category-based filtering (lendborrow, dex, bridge)
     *
     * @param request - The metadata request parameters
     * @returns Promise resolving to metadata response
     *
     * @example
     * **Get Lend/Borrow Protocol Metadata**
     * ```typescript
     * const metadata = await api.getApiMetadataV2({
     *   category: "lendborrow",
     *   endpoint: "getpool"
     * });
     *
     * metadata.data.forEach(protocol => {
     *   console.log(`${protocol.protocolName}: ${protocol.protocolLogo}`);
     * });
     * ```
     * @internal
     */
    async getApiMetadataV2(request) {
        return this.supportApi.getapimetadatav2(request);
    }
}
//# sourceMappingURL=data:application/json;base64,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